/**
 * Permission và Role Management Types
 * Hệ thống phân quyền dựa trên route-based permissions
 * 
 * Cấu trúc database:
 * - users: Bảng user chính
 * - role: Bảng role với permissions (jsonb array of objects)
 * - user_roles: Bảng liên kết many-to-many giữa users và roles
 * 
 * Permission format: JSON Object {route: string, action: string}
 * Example: {route: "users", action: "create"}, {route: "content", action: "*"}
 */

// =====================================================
// DATABASE TYPES
// =====================================================

// Permission object type - NEW FORMAT
export interface PermissionObject {
  route: string;
  action: string;
}

// Role từ database
export interface Role {
  role_id: string;
  role_name: string;
  permissions: PermissionObject[]; // jsonb array of objects trong database
  created_at: number; // Unix timestamp (int8)
  updated_at: number; // Unix timestamp (int8)
}

// User Role mapping (user_roles table)
export interface UserRole {
  user_id: string;
  role_id: string;
}

// User với role information
export interface UserWithRoles {
  user_id: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  stations?: string[];
  firebase_token?: string;
  created_at: number; // Unix timestamp (int8)
  updated_at: number; // Unix timestamp (int8)
  roles?: Role[]; // Populated from join
  wallet_amount?: number; // So du vi, lay tu bang wallet
}

// =====================================================
// PERMISSION SYSTEM
// =====================================================

// Định nghĩa các route keys - dựa trên .umirc.ts routes
export type RouteKey = 
  | 'dashboard'
  | 'content'
  | 'devices-pricing'
  | 'map'
  | 'users'
  | 'roles'
  | 'ev-stations'
  | 'gateway-stations'
  | 'gateway-devices'
  | 'reports';

// Định nghĩa các action types
export type PermissionAction = 
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'export'
  | 'manage-routes'
  | 'manage-stations'
  | 'manage-roles'
  | 'view-own'  // Xem của riêng mình (dành cho owner_station)
  | '*'; // wildcard action

// Danh sách tất cả routes trong hệ thống
export const ALL_ROUTES: Array<{key: RouteKey, name: string, path: string}> = [
  { key: 'dashboard', name: 'Tổng quan', path: '/dashboard' },
  { key: 'content', name: 'Quản lý nội dung', path: '/blogs' },
  { key: 'devices-pricing', name: 'Thiết bị - Giá điện', path: '/devices-pricing' },
  { key: 'map', name: 'Quản lý bản đồ', path: '/map' },
  { key: 'users', name: 'Quản lý người dùng', path: '/users' },
  { key: 'roles', name: 'Quản lý vai trò', path: '/roles' },
  { key: 'ev-stations', name: 'Quản lý trạm sạc', path: '/ev-stations' },
  { key: 'gateway-stations', name: 'Quản lý Gateway Station', path: '/gateway-stations' },
  { key: 'gateway-devices', name: 'Quản lý Gateway Device', path: '/gateway-devices' },
  { key: 'reports', name: 'Quản lý báo cáo', path: '/reports' },
];

// Danh sách actions cho mỗi route
export const ROUTE_ACTIONS: Record<RouteKey, PermissionAction[]> = {
  dashboard: ['view', 'export'],
  content: ['view', 'create', 'edit', 'delete'],
  'devices-pricing': ['view', 'create', 'edit', 'delete'],
  map: ['view', 'manage-routes', 'manage-stations'],
  users: ['view', 'create', 'edit', 'delete'],
  roles: ['view', 'create', 'edit', 'delete', 'manage-roles'],
  'ev-stations': ['view', 'view-own', 'create', 'edit', 'delete', 'manage-stations'],
  'gateway-stations': ['view', 'create', 'edit', 'delete'],
  'gateway-devices': ['view', 'create', 'edit', 'delete'],
  reports: ['view', 'export'],
};

// =====================================================
// HELPER FUNCTIONS - ADMIN SYSTEM
// =====================================================

// Helper function để check permission - NEW OBJECT FORMAT
export function hasPermission(
  userPermissions: PermissionObject[],
  requiredRoute: string,
  requiredAction: string
): boolean {
  // Check if user has full admin access
  if (userPermissions.some(p => p.route === '*' && p.action === '*')) {
    return true;
  }
  
  // Check exact permission match
  if (userPermissions.some(p => p.route === requiredRoute && p.action === requiredAction)) {
    return true;
  }
  
  // Check wildcard action for specific route (e.g., {route: 'users', action: '*'})
  if (userPermissions.some(p => p.route === requiredRoute && p.action === '*')) {
    return true;
  }
  
  // Check wildcard route (e.g., {route: '*', action: 'view'})
  if (userPermissions.some(p => p.route === '*' && p.action === requiredAction)) {
    return true;
  }
  
  return false;
}

// Helper function với string format để backward compatibility
export function hasPermissionString(
  userPermissions: PermissionObject[],
  requiredPermission: string
): boolean {
  if (requiredPermission === '*') {
    return userPermissions.some(p => p.route === '*' && p.action === '*');
  }
  
  const parts = requiredPermission.split('.');
  if (parts.length === 2) {
    const [route, action] = parts;
    return hasPermission(userPermissions, route, action);
  }
  
  return false;
}

// Aggregate permissions from multiple roles - NEW OBJECT FORMAT
export function aggregatePermissionsFromRoles(roles: Role[]): PermissionObject[] {
  const permissionMap = new Map<string, PermissionObject>();
  
  roles.forEach(role => {
    role.permissions?.forEach(permission => {
      const key = `${permission.route}:${permission.action}`;
      if (!permissionMap.has(key)) {
        permissionMap.set(key, permission);
      }
    });
  });
  
  return Array.from(permissionMap.values());
}

// Check if user can access a route
export function canAccessRoute(
  userPermissions: PermissionObject[],
  routeKey: RouteKey
): boolean {
  return hasPermission(userPermissions, routeKey, 'view');
}

// Get accessible routes for user
export function getAccessibleRoutes(userPermissions: PermissionObject[]): RouteKey[] {
  return ALL_ROUTES
    .map(route => route.key)
    .filter(routeKey => canAccessRoute(userPermissions, routeKey));
}

// Check if user is admin (has * permission)
export function isUserAdmin(permissions: PermissionObject[]): boolean {
  return permissions.some(p => p.route === '*' && p.action === '*');
}

// Check if user is owner_station (chỉ có quyền view-own trên ev-stations)
export function isStationOwner(permissions: PermissionObject[]): boolean {
  return permissions.some(p => p.route === 'ev-stations' && p.action === 'view-own');
}

// Check if user can view all stations (admin hoặc có quyền view)
export function canViewAllStations(permissions: PermissionObject[]): boolean {
  return isUserAdmin(permissions) || hasPermission(permissions, 'ev-stations', 'view');
}

// =====================================================
// CLIENT SYSTEM PERMISSIONS (End Users)
// =====================================================

// Định nghĩa các module cho client app
export type ClientModule = 
  | 'stations'      // Trạm sạc
  | 'booking'       // Đặt chỗ sạc
  | 'charging'      // Sạc điện
  | 'payment'       // Thanh toán
  | 'history'       // Lịch sử
  | 'review'        // Đánh giá
  | 'profile'       // Hồ sơ cá nhân
  | 'wallet'        // Ví điện tử
  | 'notification'  // Thông báo
  | 'station-management'  // Quản lý trạm (cho chủ trạm)
  | 'revenue'       // Doanh thu (cho chủ trạm)
  | 'pricing'       // Giá điện (cho chủ trạm)
  | 'analytics';    // Thống kê (cho chủ trạm)

// Định nghĩa các action cho client
export type ClientAction = 
  | 'view'          // Xem
  | 'create'        // Tạo mới
  | 'update'        // Cập nhật
  | 'delete'        // Xóa
  | 'search'        // Tìm kiếm
  | 'book'          // Đặt chỗ
  | 'cancel'        // Hủy
  | 'pay'           // Thanh toán
  | 'rate'          // Đánh giá
  | 'export';       // Xuất dữ liệu

// Format: module.action (ví dụ: stations.search, booking.create)
export type ClientPermission = `${ClientModule}.${ClientAction}` | 'client.*';

// Định nghĩa roles cho client system
export type ClientRole = 
  | 'charging_user'   // Người dùng sạc điện
  | 'station_owner'   // Chủ trạm sạc
  | 'premium_user'    // Người dùng premium (charging + benefits)
  | 'guest';          // Khách (chưa đăng ký)

// Permission mapping cho từng client role
export const CLIENT_ROLE_PERMISSIONS: Record<ClientRole, ClientPermission[]> = {
  guest: [
    'stations.view',
    'stations.search',
  ],
  
  charging_user: [
    'stations.view',
    'stations.search',
    'booking.view',
    'booking.create',
    'booking.cancel',
    'charging.view',
    'charging.create',
    'payment.view',
    'payment.pay',
    'history.view',
    'history.export',
    'review.view',
    'review.create',
    'review.update',
    'review.delete',
    'profile.view',
    'profile.update',
    'wallet.view',
    'wallet.update',
    'notification.view',
    'notification.update',
  ],
  
  premium_user: [
    'client.*',
  ],
  
  station_owner: [
    'stations.view',
    'stations.search',
    'booking.view',
    'booking.create',
    'booking.cancel',
    'charging.view',
    'charging.create',
    'payment.view',
    'payment.pay',
    'history.view',
    'history.export',
    'review.view',
    'review.create',
    'review.update',
    'review.delete',
    'profile.view',
    'profile.update',
    'wallet.view',
    'wallet.update',
    'notification.view',
    'notification.update',
    'station-management.view',
    'station-management.create',
    'station-management.update',
    'station-management.delete',
    'pricing.view',
    'pricing.update',
    'revenue.view',
    'revenue.export',
    'analytics.view',
    'analytics.export',
  ],
};

// =====================================================
// CLIENT SYSTEM HELPER FUNCTIONS
// =====================================================

export function hasClientPermission(
  userPermissions: ClientPermission[],
  requiredPermission: ClientPermission
): boolean {
  if (userPermissions.includes('client.*')) {
    return true;
  }
  
  if (userPermissions.includes(requiredPermission)) {
    return true;
  }
  
  const parts = requiredPermission.split('.');
  if (parts.length === 2) {
    const [module] = parts;
    const wildcardPermission = `${module}.*` as ClientPermission;
    if (userPermissions.includes(wildcardPermission)) {
      return true;
    }
  }
  
  return false;
}

export function getPermissionsForClientRole(role: ClientRole): ClientPermission[] {
  return CLIENT_ROLE_PERMISSIONS[role] || [];
}

export function canAccessClientModule(
  userPermissions: ClientPermission[],
  module: ClientModule,
  action: ClientAction = 'view'
): boolean {
  const permission: ClientPermission = `${module}.${action}`;
  return hasClientPermission(userPermissions, permission);
}
